import { useEffect, useState } from 'react';
import { BrandThreadUnread, getBrandThreadUnread } from '../api/payment/brand-thread.api';
import { THREAD_READ_EVENT, THREAD_UNREAD_POLL_MS } from '../utils/brand-thread';

// Số phòng trao đổi có tin chưa đọc. Một vòng hỏi dùng chung cho mọi nơi đang hiện chấm đỏ (menu trái, các dòng deal…),
// 60 giây một lần, bỏ lượt khi tab đang ẩn, hỏi ngay khi quay lại tab hoặc khi khung "Trao đổi" vừa đánh dấu đã đọc.

const EMPTY: BrandThreadUnread = { total: 0, asCreator: 0, asOrderer: 0, threadIds: [] };

let current: BrandThreadUnread = EMPTY;
const listeners = new Set<(v: BrandThreadUnread) => void>();
let timer: ReturnType<typeof setInterval> | null = null;
let inflight = false;
// Có yêu cầu hỏi lại đến lúc đang có một lượt hỏi chạy dở: chạy thêm đúng một lượt sau khi lượt đó xong.
let pending = false;
// Tăng mỗi lần người cuối cùng ngừng theo dõi (đăng xuất): phản hồi của vòng cũ về muộn thì bỏ, khỏi lộ số của người trước.
let generation = 0;

const refresh = async (): Promise<void> => {
  if (document.visibilityState !== 'visible') return;
  if (inflight) {
    pending = true;
    return;
  }
  inflight = true;
  const startedIn = generation;
  try {
    const result = await getBrandThreadUnread();
    if (startedIn === generation) {
      current = result;
      listeners.forEach((l) => l(current));
    }
  } catch {
    // Giữ số cũ, lượt sau hỏi lại.
  } finally {
    inflight = false;
  }
  if (pending) {
    pending = false;
    if (listeners.size > 0) await refresh();
  }
};

export const refreshBrandThreadUnread = (): void => {
  refresh();
};

const onVisible = () => {
  if (document.visibilityState === 'visible') refresh();
};

const subscribe = (listener: (v: BrandThreadUnread) => void) => {
  listeners.add(listener);
  if (listeners.size === 1) {
    refresh();
    timer = setInterval(refreshBrandThreadUnread, THREAD_UNREAD_POLL_MS);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener(THREAD_READ_EVENT, refreshBrandThreadUnread);
  } else {
    listener(current);
  }
  return () => {
    listeners.delete(listener);
    if (listeners.size > 0) return;
    if (timer) clearInterval(timer);
    timer = null;
    document.removeEventListener('visibilitychange', onVisible);
    window.removeEventListener(THREAD_READ_EVENT, refreshBrandThreadUnread);
    current = EMPTY;
    generation += 1;
    pending = false;
  };
};

export const useBrandThreadUnread = (enabled = true): BrandThreadUnread => {
  const [value, setValue] = useState<BrandThreadUnread>(current);
  useEffect(() => (enabled ? subscribe(setValue) : undefined), [enabled]);
  return enabled ? value : EMPTY;
};
